import React, { Component } from 'react';
import { twMerge } from 'tailwind-merge';

import BetaBadge from '../../../../common/components/Badge';
import Collapse from '../../../../common/components/Collapse/Collapse';
import { SearchFilterContentTypes } from '../../../Shared/enums';
import { FilterCategories } from '../../containers/MenuContainerInterfaces';
import ArrowIcon from './ArrowIcon';
import FilterRadioOption from './FilterRadioOption';

interface Props {
  name: string;
  filterName?: string;
  filterValues?: object[];
  checked?: FilterCategories[] | SearchFilterContentTypes | string;
  showOptionBoxesOnPageLoad?: boolean;
  numberOfChoicesToShowInitially?: number;
  alphabetizeOptions?: boolean;
  invalidSelectionMessage?: string;
  isCollapsed?: boolean;
  toggleCollapsed: () => void;
  handleFilterChanged: (filterName: string, filterValue: string) => void;
  hideValue?: boolean;
  badgeText?: string;
  badgeClassName?: string;
}

export default class CollapsibleRadioFilter extends Component<Props> {
  static defaultProps = {
    invalidSelectionMessage: null,
  };

  render() {
    const {
      name,
      checked,
      filterValues,
      alphabetizeOptions,
      isCollapsed,
      hideValue,
      toggleCollapsed,
      invalidSelectionMessage,
      badgeText,
      badgeClassName,
    } = this.props;

    const htmlSafeName = this.getHtmlSafeName(name);
    const filteredItems = this.getFilteredItems(
      filterValues,
      alphabetizeOptions
    );
    const isClosed = isCollapsed || Boolean(invalidSelectionMessage);

    const ariaControlId = htmlSafeName + '-container';
    const ariaLabelId = `filter-heading-${htmlSafeName}`;

    return (
      <div
        id={`filter-group-${htmlSafeName}`}
        className={isClosed ? 'collapsed' : 'expanded'}
      >
        <div
          onClick={toggleCollapsed}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleCollapsed();
              e.preventDefault();
            }
          }}
          aria-controls={
            this.props.invalidSelectionMessage ? undefined : ariaControlId
          }
          role={this.props.invalidSelectionMessage ? 'presentation' : 'button'}
          aria-expanded={
            this.props.invalidSelectionMessage ? undefined : !isClosed
          }
          tabIndex={this.props.invalidSelectionMessage ? undefined : 0}
        >
          <h2
            className={twMerge(
              this.getHeadingClassName(),
              badgeText || hideValue ? 'flex items-center gap-2' : 'block'
            )}
            id={ariaLabelId}
          >
            {badgeText && (
              <BetaBadge badgeText={badgeText} className={badgeClassName} />
            )}
            {name}
            <ArrowIcon
              isCollapsed={isCollapsed}
              invalidSelectionMessage={this.props.invalidSelectionMessage}
            />
          </h2>
        </div>

        {this.renderInvalidSelectionMessage() || (
          <>
            <Collapse isCollapsed={isClosed} controlId={ariaControlId}>
              <fieldset
                aria-labelledby={ariaLabelId}
                className={`${isCollapsed ? 'invisible' : ''}`}
              >
                {filteredItems.map((item) => {
                  const { value, label, tooltip } = item;
                  return (
                    <FilterRadioOption
                      key={value}
                      id={value}
                      label={label}
                      isChecked={checked === value}
                      name={name}
                      onSelectOption={() => this.onSelectOption(value)}
                      tooltip={tooltip}
                    />
                  );
                })}
              </fieldset>
            </Collapse>

            {isCollapsed && !hideValue && (
              <div className="collapsible-filter-selected-option">
                {this.getCheckedLabels(filterValues, checked)}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  getHeadingClassName() {
    return twMerge(
      'heading',
      this.props.invalidSelectionMessage !== null && 'invalid'
    );
  }

  renderInvalidSelectionMessage() {
    return this.props.invalidSelectionMessage ? (
      <span className="invalid-message">
        {this.props.invalidSelectionMessage}
      </span>
    ) : null;
  }

  getCheckedLabels(items, selectedValue) {
    return items
      .filter((item) => {
        return item.value === selectedValue;
      })
      .map((item) => {
        return item.label;
      });
  }

  onSelectOption(filterValue) {
    this.props.handleFilterChanged(this.props.filterName, filterValue);
  }

  getFilteredItems(items, alphabetize) {
    const clonedItems = items.slice(0);

    if (alphabetize) {
      clonedItems.sort((a, b) => {
        return a.label > b.label ? 1 : -1;
      });
    }

    return clonedItems.splice(0, items.length);
  }

  getHtmlSafeName(rawName) {
    return rawName.replace(/\W/g, '');
  }
}
